'use client'

import { useCallback, useContext, useEffect, useRef, useState } from 'react'

import {
  AdPage,
  AdShape,
  visibleDurationEvent,
  ClickAdEventTarget,
} from '@marketplace-web/ads/ads-data'
import { ConsentGroup, useIsConsentGroupEnabled } from '@marketplace-web/consent/consent-feature'
import { useUserAgent } from '@marketplace-web/environment/request-context-data'
import { useSystemConfiguration } from '@marketplace-web/shared/system-configuration-data'
import { CountryCode } from '@marketplace-web/country/country-data'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'

import VanOfferContent from './VanOfferContent'
import AdsContext from '../../../containers/AdsProvider/AdsContext'
import { useShouldShowRokt } from '../RoktAdvertisement'
import useShouldShowVanOffer from '../../../hooks/useShouldShowVanOffer'
import useFetchVanPlacement from '../../../hooks/useFetchVanPlacement'
import useVisibleTimeTracker from '../../../hooks/useVisibleTimeTracker'
import useClickAdEventTracking from '../../../hooks/useClickAdEventTracking'

// This is hardcoded at the moment as we don't have placement for this specific ad
const placementId = 'web_purchase_van'

const VanOfferAdvertisement = () => {
  const [isOpen, setIsOpen] = useState(true)
  const hasConsentedTargeting = useIsConsentGroupEnabled(ConsentGroup.VanAdvertising)
  const { transaction, conversation } = useConversationContext()
  const isDebitProcessed = transaction?.isDebitProcessed
  const purchaseId = conversation?.transaction?.purchaseId

  const userAgent = useUserAgent()
  const { getAdsPlatform } = useContext(AdsContext)
  const { userCountry: countryCode } = useSystemConfiguration()

  const { shouldShowVanOffer, setShouldShowVan } = useShouldShowVanOffer()
  const { setShouldShowRokt, hasRoktBeenShown } = useShouldShowRokt()

  const { startTimingVisible, stopAndGetVisibleSeconds } = useVisibleTimeTracker()
  const { trackClickAdEvent } = useClickAdEventTracking()
  const isImpressionTrackingEnabled = useFeatureSwitch('web_van_offer_duration_tracking')
  const hasTrackedRef = useRef(false)
  const { track } = useTracking()

  const { placement, fetchVanAdPlacement } = useFetchVanPlacement({
    placementId,
    shape: AdShape.Rectangle,
    consentStatus: hasConsentedTargeting,
    page: AdPage.Messages,
    platform: getAdsPlatform(userAgent),
    countryCode: countryCode as CountryCode,
  })

  const handleTrackAndClose = useCallback(() => {
    if (!isOpen || hasTrackedRef.current) return

    hasTrackedRef.current = true

    if (placement?.correlationId && placement.id && isImpressionTrackingEnabled) {
      const visibleSeconds = stopAndGetVisibleSeconds()
      track(
        visibleDurationEvent({
          campaignId: placement.id,
          correlationId: placement.correlationId,
          duration: visibleSeconds,
        }),
      )
    }
  }, [isOpen, placement, isImpressionTrackingEnabled, stopAndGetVisibleSeconds, track])

  const onClose = (target: ClickAdEventTarget) => {
    trackClickAdEvent(placement, target)
    handleTrackAndClose()

    setIsOpen(false)
  }

  const shouldRunVanAd = shouldShowVanOffer && isDebitProcessed && !hasRoktBeenShown

  useEffect(() => {
    if (!shouldRunVanAd) return

    fetchVanAdPlacement(purchaseId)
    setShouldShowVan(false)
  }, [fetchVanAdPlacement, setShouldShowVan, shouldRunVanAd, purchaseId])

  useEffect(() => {
    if (!shouldShowVanOffer) return

    setShouldShowRokt(false)
  }, [shouldShowVanOffer, setShouldShowRokt])

  useEffect(() => {
    if (!placement?.creative) return

    startTimingVisible()
  }, [placement, startTimingVisible])

  useEffect(() => {
    // For full page unload (back/refresh)
    window.addEventListener('beforeunload', handleTrackAndClose)

    return () => {
      window.removeEventListener('beforeunload', handleTrackAndClose)
    }
  }, [handleTrackAndClose])

  if (!placement?.creative) return null

  return (
    <div data-testid="van-offer-content">
      <VanOfferContent
        isOpen={isOpen}
        onClose={onClose}
        config={placement}
        creative={placement.creative}
      />
    </div>
  )
}

export default VanOfferAdvertisement
